import { Button, EmptyState, Field, formatDate, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import {
  type ApiError,
  asApiError,
  changeMemberRole,
  fetchMembers,
  type Invitation,
  inviteMember,
  MEMBER_ROLES,
  type MemberRole,
  type MembersPayload,
  removeMember,
  resetMemberTwoFactor,
  revokeInvitation,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useCan, useCurrentSession } from '../lib/session'

/**
 * Members and roles (M3-02). Everything on this page is gated twice: the API decides, and the
 * matrix it sends along decides what is drawn. A viewer therefore sees the list and no controls,
 * a developer never gets here at all, and the last owner's row keeps its select and its remove
 * button disabled — the same 409 the API would answer, one click earlier.
 */
export const Route = createFileRoute('/_app/settings/members')({ component: Members })

function Members() {
  const { t, locale } = useConsoleI18n()
  const can = useCan()
  const session = useCurrentSession()
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: MembersPayload }
    | { status: 'error'; error: ApiError }
  >({ status: 'loading' })
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)

  const reload = useCallback(async () => {
    try {
      setState({ status: 'ready', data: await fetchMembers() })
    } catch (cause) {
      setState({ status: 'error', error: asApiError(cause) })
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(undefined)
      setNotice(undefined)
      try {
        await action()
        await reload()
        return true
      } catch (cause) {
        const failure = asApiError(cause)
        setError(failure.problem?.detail ?? t(failure.messageKey))
        return false
      }
    },
    [reload, t],
  )

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }

  if (state.status === 'error') {
    return state.error.status === 403 ? (
      <EmptyState title={t('members.forbidden.title')} description={t('members.forbidden.body')} />
    ) : (
      <EmptyState title={t('error.title')} description={t(state.error.messageKey)} />
    )
  }

  const { members, invitations } = state.data
  const manage = can('members:manage')
  const owners = members.filter((entry) => entry.role === 'owner').length

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h2 className="font-semibold text-lead text-ink">{t('members.title')}</h2>
        <p className="text-body text-muted">{t('members.intro')}</p>
      </div>

      {error === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      )}
      {notice === undefined ? null : (
        <p role="status" className="text-body text-muted">
          {notice}
        </p>
      )}

      <table className="w-full border-collapse text-left text-body">
        <thead>
          <tr className="border-line border-b text-caption text-muted">
            <th scope="col" className="py-2 pr-4 font-medium">
              {t('members.column.member')}
            </th>
            <th scope="col" className="py-2 pr-4 font-medium">
              {t('members.column.role')}
            </th>
            <th scope="col" className="py-2 font-medium">
              {t('members.column.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {members.map((entry) => {
            const lastOwner = entry.role === 'owner' && owners <= 1
            const locked = !manage || lastOwner

            return (
              <tr key={entry.id} className="border-line border-b align-middle">
                <td className="py-3 pr-4">
                  <span className="block font-medium text-ink">
                    {entry.name}
                    {entry.user_id === session.user.id ? ` (${t('members.you')})` : ''}
                  </span>
                  <span className="block break-all text-caption text-muted">{entry.email}</span>
                </td>
                <td className="py-3 pr-4">
                  {manage ? (
                    <select
                      aria-label={t('members.role.label', { name: entry.name })}
                      value={entry.role}
                      disabled={locked}
                      onChange={(event) =>
                        void run(() => changeMemberRole(entry.id, event.target.value as MemberRole))
                      }
                      className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink disabled:opacity-50"
                    >
                      {MEMBER_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {t(`members.role.${role}`)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    t(`members.role.${entry.role}`)
                  )}
                </td>
                <td className="py-3">
                  {manage ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="ghost"
                        disabled={locked}
                        title={lastOwner ? t('members.last_owner') : undefined}
                        onClick={() => {
                          if (!window.confirm(t('members.remove.confirm', { name: entry.name })))
                            return
                          void run(() => removeMember(entry.id))
                        }}
                      >
                        {t('members.remove')}
                      </Button>
                      {/*
                        The help desk of M4-12, with a rule instead of a judgement call. Not offered
                        on your own row: clearing your own factor is the button on the security page
                        and that one asks for a code, which is the whole difference between the two.
                      */}
                      {entry.user_id === session.user.id ? null : (
                        <Button
                          variant="ghost"
                          onClick={() => {
                            if (
                              !window.confirm(
                                t('members.reset_two_factor.confirm', { name: entry.name }),
                              )
                            )
                              return
                            void run(() => resetMemberTwoFactor(entry.id))
                          }}
                        >
                          {t('members.reset_two_factor')}
                        </Button>
                      )}
                    </div>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      {members.length === 1 ? <p className="text-body text-muted">{t('members.empty')}</p> : null}
      {manage ? <p className="text-caption text-muted">{t('members.last_owner')}</p> : null}

      {manage ? (
        <InviteForm
          onInvite={async (email, role) => {
            const ok = await run(() => inviteMember({ email, role }))
            if (ok) setNotice(t('invite.sent', { email }))
            return ok
          }}
        />
      ) : null}

      {manage ? (
        <section className="flex flex-col gap-3">
          <h2 className="font-semibold text-lead text-ink">{t('invitations.title')}</h2>
          {invitations.length === 0 ? (
            <p className="text-body text-muted">{t('invitations.empty')}</p>
          ) : (
            <ul className="flex list-none flex-col gap-2 p-0">
              {invitations.map((invitation: Invitation) => (
                <li
                  key={invitation.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-line px-3 py-2"
                >
                  <span className="flex flex-col">
                    <span className="break-all font-medium text-body text-ink">
                      {invitation.email}
                    </span>
                    <span className="text-caption text-muted">
                      {t(`members.role.${invitation.role}`)} ·{' '}
                      {t('invitations.expires', {
                        expires: formatDate(invitation.expires_at, locale),
                      })}
                    </span>
                  </span>
                  <Button
                    variant="ghost"
                    onClick={() => void run(() => revokeInvitation(invitation.id))}
                  >
                    {t('invitations.revoke')}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  )
}

function InviteForm({
  onInvite,
}: {
  readonly onInvite: (email: string, role: MemberRole) => Promise<boolean>
}) {
  const { t } = useConsoleI18n()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<MemberRole>('viewer')
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    const sent = await onInvite(email.trim(), role)
    setBusy(false)
    if (sent) setEmail('')
  }

  return (
    <form
      className="flex flex-col gap-4 rounded-card border border-line p-4"
      onSubmit={(event) => void onSubmit(event)}
    >
      <h2 className="font-semibold text-lead text-ink">{t('invite.title')}</h2>
      <div className="flex flex-col gap-4 md:flex-row md:items-end">
        <Field label={t('invite.email.label')} required className="grow">
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="min-h-touch w-full rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Field label={t('invite.role.label')} hint={t(`members.role.${role}.hint`)}>
          {({ id, describedBy }) => (
            <select
              id={id}
              aria-describedby={describedBy}
              value={role}
              onChange={(event) => setRole(event.target.value as MemberRole)}
              className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
            >
              {MEMBER_ROLES.map((value) => (
                <option key={value} value={value}>
                  {t(`members.role.${value}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Button type="submit" busy={busy} busyLabel={t('loading')}>
          {t('invite.submit')}
        </Button>
      </div>
    </form>
  )
}
